import { SavedSearch, SavedSearchPage } from '@creditsights/api-ui-web';
import { Button, ButtonMode, ButtonSize, Icon, ICONS, Popup, Tooltip } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useMemo, useRef, useState } from 'react';
import styles from './SavedSearchesDropdown.module.scss';
import useFetchSavedSearches from '../../hooks/searches/useFetchSavedSearches';
import useSelectedSavedSearch from '../../hooks/searches/useSelectedSavedSearch';
import { trackOnClickSavedSearches } from '../../utils/trackUsageUtils';

const cx = classNames.bind(styles);

interface SavedSearchesDropdownProps {
  onSearchSelect: (search: SavedSearch) => void;
  pageName: SavedSearchPage;
}

const SavedSearchesDropdown = ({ onSearchSelect, pageName }: SavedSearchesDropdownProps): React.ReactElement => {
  const { data, isFetching: isLoadingSearches } = useFetchSavedSearches();
  const [open, setOpen] = useState(false);
  const buttonLabelSpanRef = useRef<HTMLSpanElement>(null);
  const [selectedSearchId] = useSelectedSavedSearch();

  const savedSearches = useMemo(() => data?.filter((search) => search.pageName === pageName), [data, pageName]);
  const hasSavedSearches = savedSearches && savedSearches.length > 0;

  const selectedSearch = !isLoadingSearches
    ? savedSearches?.find((search) => search.id === selectedSearchId)
    : undefined;

  const buttonLabel = selectedSearch ? selectedSearch.name : 'Saved Searches';

  const ellipsisActive =
    buttonLabelSpanRef.current && buttonLabelSpanRef.current.offsetWidth < buttonLabelSpanRef.current.scrollWidth;

  const dropdownButton = (
    <div>
      <Tooltip
        content={hasSavedSearches ? buttonLabel : 'There are no saved searches'}
        disabled={isLoadingSearches || (hasSavedSearches && !ellipsisActive)}
      >
        <div>
          <Button
            loading={isLoadingSearches}
            disabled={isLoadingSearches || !hasSavedSearches}
            mode={ButtonMode.SECONDARY}
            size={ButtonSize.SMALL}
            ariaLabel={buttonLabel}
            dataTestId="saved-searches-dropdown"
            className={cx('dropdown-button')}
          >
            <span ref={buttonLabelSpanRef} className={cx('button-label')}>
              {buttonLabel}
            </span>
            <span className={cx('caret-icon', { 'is-open': open })}>
              <Icon name={ICONS.CaretDown} width={10} height={10} />
            </span>
          </Button>
        </div>
      </Tooltip>
    </div>
  );

  const onSelect = (search: SavedSearch) => {
    setOpen(false);
    onSearchSelect(search);

    trackOnClickSavedSearches(search.name);
  };

  return (
    <Popup
      position="bottom right"
      className={cx('dropdown')}
      trigger={dropdownButton}
      on="click"
      open={open}
      onOpen={() => !isLoadingSearches && hasSavedSearches && setOpen(true)}
      onClose={() => setOpen(false)}
      data-testid="saved-searches-popup"
    >
      {hasSavedSearches && (
        <div className={cx('popup-content')}>
          {savedSearches?.map((search) => {
            return (
              <Button
                key={search.id}
                className={cx('saved-search-btn', { selected: search.id === selectedSearchId })}
                onClick={() => onSelect(search)}
                ariaLabel={search.name}
              >
                <span>{search.name}</span>
                {search.id === selectedSearchId && (
                  <div className={cx('icon-wrapper')}>
                    <Icon height={10} width={10} name={ICONS.Checkmark} />
                  </div>
                )}
              </Button>
            );
          })}
        </div>
      )}
    </Popup>
  );
};

export default SavedSearchesDropdown;
